/**
 * Legal — the read seam, over a host's own Firestore and Storage bucket. The
 * content-addressed pointer on the public workspace card
 * (`Workspace.Workspace["legalContent"]`) resolves each locale's source text
 * by a plain, unauthenticated `fetch` against its Storage blob — no SDK, no
 * App Check, the one request on the page an edge cache can serve without a
 * round trip to this project. `legalKindResolutions$` keeps `unpublished` (no
 * pointer) distinct from `failed` (the fetch itself broke); `legalContent$`
 * keeps that line for the public reader too: an unpublished kind reads as the
 * platform default, while a published text that could not be fetched answers
 * `"failed"`, since the organization's own text exists and the default must
 * not stand in for it. On the bare host, where no organization is open, the
 * operator's own record and texts are read instead (`platformLegal$`,
 * `platformLegalContent$`). legal.sync.ts is the reader; Revent web's admin
 * editor reads `legalKindResolutions$` for its seed step.
 */

import { retryOnTransient, snapshotDoc$ } from "@mhmo91/rx";
import type { LegalContent, LegalDocumentKind, Locale } from "@momo/i18n";
import { Platform, type Workspace } from "@momo/types";
import { doc, type Firestore } from "firebase/firestore";
import {
	catchError,
	combineLatest,
	filter,
	from,
	map,
	type Observable,
	of,
} from "rxjs";
import { z } from "zod";

/** Thrown when a legal blob's `fetch` resolves with a non-2xx status — the
 *  `status` field is what `retryOnTransient` (@mhmo91/rx) reads to tell an
 *  infra flake (429/5xx) apart from a genuine miss (404). */
class LegalBlobFetchError extends Error {
	status: number;
	constructor(status: number, url: string) {
		super(`legal blob fetch failed: ${status} ${url}`);
		this.status = status;
	}
}

/** One kind's real outcome — "no pointer was ever written" is a fact,
 *  distinct from "the blob fetch failed", which is an unknown, and a
 *  consumer deciding whether a kind is genuinely unpublished must never
 *  treat `"failed"` as `"unpublished"`. */
export type LegalKindResolution =
	| { readonly status: "unpublished" }
	| {
			readonly status: "resolved";
			readonly version: string;
			readonly de: string;
			readonly en: string;
	  }
	| { readonly status: "failed" };

const UNPUBLISHED_KIND: LegalContent["terms"] = {
	version: null,
	de: null,
	en: null,
};

export function legalBlobPath(
	orgId: string,
	kind: LegalDocumentKind,
	locale: Locale,
	sha: string,
): string {
	return `workspaces/${orgId}/legal/${kind}/${locale}/${sha}.json`;
}

function collapseResolutions({
	terms,
	privacy,
}: {
	readonly terms: LegalKindResolution;
	readonly privacy: LegalKindResolution;
}): LegalContent | "failed" {
	return terms.status === "failed" || privacy.status === "failed"
		? "failed"
		: {
				terms:
					terms.status === "resolved"
						? { version: terms.version, de: terms.de, en: terms.en }
						: UNPUBLISHED_KIND,
				privacy:
					privacy.status === "resolved"
						? { version: privacy.version, de: privacy.de, en: privacy.en }
						: UNPUBLISHED_KIND,
				// Nothing on this read path resolves the archive's publish instant.
				publishedAt: null,
			};
}

/** The legal reads over a host's Firestore and the bucket its blobs live in. */
export function legalReads({
	db,
	storageBucket,
}: {
	readonly db: Firestore;
	readonly storageBucket: string | undefined;
}) {
	// The bootstrap producer validates the complete emulator connection; legal
	// blobs use its Storage endpoint through the same public REST read as production.
	const emulator = import.meta.env.DEV
		? z
				.object({
					host: z.string().min(1),
					storage: z.number().int().min(1).max(65535),
				})
				.default({ host: "127.0.0.1", storage: 9219 })
				.parse(Reflect.get(globalThis, "__FIREBASE_EMULATOR_ENDPOINTS__"))
		: null;
	const storageOrigin = emulator
		? `http://${emulator.host === "::1" ? "[::1]" : emulator.host}:${emulator.storage}`
		: "https://firebasestorage.googleapis.com";

	/** One locale's blob, validated as a string. `retryOnTransient` absorbs an
	 *  infra flake (429/5xx); a genuine 404 or an offline network propagates
	 *  unretried. */
	function fetchLegalBlob$(path: string): Observable<string> {
		const url = `${storageOrigin}/v0/b/${storageBucket}/o/${encodeURIComponent(path)}?alt=media`;
		return from(
			fetch(url).then(async (res) => {
				if (!res.ok) throw new LegalBlobFetchError(res.status, url);
				return z.string().parse(await res.json());
			}),
		).pipe(retryOnTransient());
	}

	/** `"resolved"` when both locale blobs fetched, paired with the pointer's
	 *  own version stamp; `"unpublished"` with no pointer; `"failed"` when a
	 *  fetch ultimately failed. Never throws. */
	function fetchKind$(
		blobPath: (locale: Locale, sha: string) => string,
		kind: LegalDocumentKind,
		pointer: Workspace.LegalContentPointer | null,
	): Observable<LegalKindResolution> {
		if (pointer === null) return of({ status: "unpublished" });
		return combineLatest([
			fetchLegalBlob$(blobPath("de", pointer.de)),
			fetchLegalBlob$(blobPath("en", pointer.en)),
		]).pipe(
			map(
				([de, en]): LegalKindResolution => ({
					status: "resolved",
					version: pointer.version,
					de,
					en,
				}),
			),
			catchError((err: unknown) => {
				console.warn(`[legal] ${kind} blob fetch failed`, err);
				return of<LegalKindResolution>({ status: "failed" });
			}),
		);
	}

	function legalKindResolutions$(
		orgId: string,
		pointer: Workspace.Workspace["legalContent"],
	): Observable<{
		readonly terms: LegalKindResolution;
		readonly privacy: LegalKindResolution;
	}> {
		return combineLatest([
			fetchKind$(
				(locale, sha) => legalBlobPath(orgId, "terms", locale, sha),
				"terms",
				pointer?.terms ?? null,
			),
			fetchKind$(
				(locale, sha) => legalBlobPath(orgId, "privacy", locale, sha),
				"privacy",
				pointer?.privacy ?? null,
			),
		]).pipe(map(([terms, privacy]) => ({ terms, privacy })));
	}

	return {
		legalKindResolutions$,

		/** The resolved override for both kinds, or `"failed"` when a published
		 *  text could not be fetched. */
		legalContent$: (
			orgId: string,
			pointer: Workspace.Workspace["legalContent"],
		): Observable<LegalContent | "failed"> =>
			legalKindResolutions$(orgId, pointer).pipe(map(collapseResolutions)),

		/** `platform/legal`, live: the deployment operator a founder contracts
		 *  with on the bare host, or null once the server says no record exists.
		 *  A miss served from the local cache (offline) says nothing about the
		 *  server, and absence refuses sign-up, so it is no answer yet; the
		 *  server's confirmation of a cached miss changes metadata alone, hence
		 *  the metadata listener. An incomplete record throws. */
		platformLegal$: (): Observable<Platform.PlatformLegal | null> =>
			snapshotDoc$(doc(db, "platform", "legal"), {
				includeMetadataChanges: true,
			}).pipe(
				filter((snap) => snap.exists() || !snap.metadata.fromCache),
				map((snap) => Platform.PlatformLegalSnapshotSchema.parse(snap.data())),
			),

		/** The operator's own terms and privacy notice at the content addresses
		 *  its record names, or `"failed"`. Both pointers are required on the
		 *  record, so the platform default never stands in for its text. */
		platformLegalContent$: (
			record: Platform.PlatformLegal,
		): Observable<LegalContent | "failed"> =>
			combineLatest([
				fetchKind$(
					(locale, sha) => Platform.platformLegalBlobPath("terms", locale, sha),
					"terms",
					record.terms,
				),
				fetchKind$(
					(locale, sha) =>
						Platform.platformLegalBlobPath("privacy", locale, sha),
					"privacy",
					record.privacy,
				),
			]).pipe(
				map(([terms, privacy]) => collapseResolutions({ terms, privacy })),
			),
	};
}

export type LegalReads = ReturnType<typeof legalReads>;
